import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../services/auth.jsx'
import { api } from '../services/api.js'

const MAINTENANCE_TYPES = ['Troca de óleo', 'Revisão', 'Reparo', 'Lubrificação', 'Filtros', 'Pneus', 'Outro']

function fmtDate(iso) {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

function fmtNum(n, decimals = 1) {
  if (n == null) return '—'
  return Number(n).toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

function fmtCurrency(n) {
  if (n == null) return '—'
  return Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

// ── Styles ────────────────────────────────────────────────────────────────────

const pageWrap = { minHeight: '100vh', background: '#f0fdf4', fontFamily: 'sans-serif' }
const header = { background: '#14532d', color: '#fff', padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, height: 56, flexShrink: 0 }
const navLink = { color: '#bbf7d0', textDecoration: 'none', fontWeight: 600, fontSize: 14, padding: '6px 12px', borderRadius: 8 }
const content = { maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }
const card = { background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.08)', padding: 24 }
const tabBar = { display: 'flex', gap: 8, marginBottom: 24 }
const greenBtn = { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }
const grayBtn = { background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }
const redBtn = { background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }
const smallGrayBtn = { background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 6, padding: '4px 10px', fontWeight: 600, fontSize: 12, cursor: 'pointer' }
const inputStyle = { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, width: '100%', boxSizing: 'border-box' }
const selectStyle = { ...inputStyle }
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }
const row = { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }
const formGroup = (w) => ({ flex: w ? `0 0 ${w}` : 1, minWidth: 100 })

// ── Overlay modal ─────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#14532d' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function TabBtn({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '9px 20px', borderRadius: 9, border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer',
        background: active ? '#14532d' : '#e5e7eb', color: active ? '#fff' : '#374151',
      }}
    >
      {label}
    </button>
  )
}

function MachineBadge({ type }) {
  const isTrator = type === 'trator'
  return (
    <span style={{ background: isTrator ? '#dbeafe' : '#fef9c3', color: isTrator ? '#1d4ed8' : '#854d0e', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
      {isTrator ? 'Trator' : 'Implemento'}
    </span>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function MecanizacaoPage() {
  const { user, logout, currentFazenda } = useAuth()
  const isAdmin = user?.role === 'admin'
  const canManageMachines = isAdmin || user?.role === 'mecanizacao'
  const fazendaId = currentFazenda?.id

  const [tab, setTab] = useState('maquinas')
  const [machines, setMachines] = useState([])
  const [areas, setAreas] = useState([])
  const [activities, setActivities] = useState([])
  const [usage, setUsage] = useState([])
  const [maintenance, setMaintenance] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // ── Filters ───────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({ machine_id: '', area_id: '', activity_key: '', date_from: '', date_to: '' })
  const [maintFilter, setMaintFilter] = useState({ machine_id: '' })

  // ── Machine form ──────────────────────────────────────────────────────────
  const [showMachineForm, setShowMachineForm] = useState(false)
  const [editingMachine, setEditingMachine] = useState(null)
  const [machineForm, setMachineForm] = useState({ name: '', type: 'trator', power_cv: '', fuel_consumption_per_hour: '', description: '' })
  const [savingMachine, setSavingMachine] = useState(false)
  const [deletingMachineId, setDeletingMachineId] = useState(null)

  // ── Usage form ────────────────────────────────────────────────────────────
  const [showUsageForm, setShowUsageForm] = useState(false)
  const [usageForm, setUsageForm] = useState({ machine_id: '', implement_id: '', area_id: '', activity_key: '', date: '', hours: '', fuel_liters: '', other_costs: '', notes: '' })
  const [savingUsage, setSavingUsage] = useState(false)
  const [deletingUsageId, setDeletingUsageId] = useState(null)

  // ── Maintenance form ──────────────────────────────────────────────────────
  const [showMaintForm, setShowMaintForm] = useState(false)
  const [maintForm, setMaintForm] = useState({ machine_id: '', type: MAINTENANCE_TYPES[0], date: '', hourmeter: '', cost: '', notes: '' })
  const [savingMaint, setSavingMaint] = useState(false)
  const [deletingMaintId, setDeletingMaintId] = useState(null)

  // ── Load ──────────────────────────────────────────────────────────────────
  async function fetchBase() {
    if (!fazendaId) { setLoading(false); return }
    setLoading(true)
    try {
      const [m, a, acts] = await Promise.all([
        api.getMachines(fazendaId),
        api.getAreas(fazendaId),
        api.getActivities(),
      ])
      setMachines(m)
      setAreas(a)
      setActivities(acts)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  async function fetchUsage() {
    if (!fazendaId) return
    try {
      const data = await api.getMachineUsage(filters, fazendaId)
      setUsage(data)
    } catch (e) { setError(e.message) }
  }

  async function fetchMaintenance() {
    if (!fazendaId) return
    try {
      const data = await api.getMachineMaintenance(maintFilter, fazendaId)
      setMaintenance(data)
    } catch (e) { setError(e.message) }
  }

  useEffect(() => { fetchBase() }, [fazendaId])
  useEffect(() => { if (tab === 'uso') fetchUsage() }, [tab, fazendaId])
  useEffect(() => { if (tab === 'manutencao') fetchMaintenance() }, [tab, fazendaId])

  // ── Machine handlers ──────────────────────────────────────────────────────
  function openNewMachine() {
    setEditingMachine(null)
    setMachineForm({ name: '', type: 'trator', power_cv: '', fuel_consumption_per_hour: '', description: '' })
    setShowMachineForm(true)
  }

  function openEditMachine(m) {
    setEditingMachine(m)
    setMachineForm({
      name: m.name, type: m.type,
      power_cv: m.power_cv ?? '', fuel_consumption_per_hour: m.fuel_consumption_per_hour ?? '',
      description: m.description ?? '',
    })
    setShowMachineForm(true)
  }

  async function handleSaveMachine() {
    if (!machineForm.name.trim()) return
    setSavingMachine(true)
    try {
      const payload = {
        name: machineForm.name.trim(),
        type: machineForm.type,
        power_cv: machineForm.power_cv !== '' ? Number(machineForm.power_cv) : null,
        fuel_consumption_per_hour: machineForm.fuel_consumption_per_hour !== '' ? Number(machineForm.fuel_consumption_per_hour) : null,
        description: machineForm.description.trim() || null,
      }
      if (editingMachine) {
        const updated = await api.updateMachine(editingMachine.id, payload, fazendaId)
        setMachines(prev => prev.map(m => m.id === updated.id ? updated : m))
      } else {
        const created = await api.createMachine(payload, fazendaId)
        setMachines(prev => [...prev, created])
      }
      setShowMachineForm(false)
    } catch (e) { setError(e.message) }
    finally { setSavingMachine(false) }
  }

  async function handleDeleteMachine(m) {
    try {
      await api.deleteMachine(m.id, fazendaId)
      setMachines(prev => prev.filter(x => x.id !== m.id))
      setDeletingMachineId(null)
    } catch (e) { setError(e.message); setDeletingMachineId(null) }
  }

  async function handleToggleMachine(m) {
    try {
      const updated = await api.updateMachine(m.id, { active: !m.active }, fazendaId)
      setMachines(prev => prev.map(x => x.id === updated.id ? updated : x))
    } catch (e) { setError(e.message) }
  }

  // ── Usage handlers ────────────────────────────────────────────────────────
  function openNewUsage() {
    setUsageForm({ machine_id: '', implement_id: '', area_id: '', activity_key: '', date: new Date().toISOString().split('T')[0], hours: '', fuel_liters: '', other_costs: '', notes: '' })
    setShowUsageForm(true)
  }

  function handleUsageHoursChange(hours) {
    const selectedMachine = machines.find(m => m.id === Number(usageForm.machine_id))
    const autoFuel = selectedMachine?.fuel_consumption_per_hour && hours
      ? (Number(hours) * selectedMachine.fuel_consumption_per_hour).toFixed(1)
      : usageForm.fuel_liters
    setUsageForm(prev => ({ ...prev, hours, fuel_liters: autoFuel }))
  }

  function handleUsageMachineChange(machineId) {
    const selectedMachine = machines.find(m => m.id === Number(machineId))
    const autoFuel = selectedMachine?.fuel_consumption_per_hour && usageForm.hours
      ? (Number(usageForm.hours) * selectedMachine.fuel_consumption_per_hour).toFixed(1)
      : ''
    setUsageForm(prev => ({ ...prev, machine_id: machineId, fuel_liters: autoFuel }))
  }

  async function handleSaveUsage() {
    if (!usageForm.machine_id || !usageForm.area_id || !usageForm.activity_key || !usageForm.date) return
    setSavingUsage(true)
    try {
      const payload = {
        machine_id: Number(usageForm.machine_id),
        implement_id: usageForm.implement_id ? Number(usageForm.implement_id) : null,
        area_id: Number(usageForm.area_id),
        activity_key: usageForm.activity_key,
        date: usageForm.date,
        hours: usageForm.hours !== '' ? Number(usageForm.hours) : null,
        fuel_liters: usageForm.fuel_liters !== '' ? Number(usageForm.fuel_liters) : null,
        other_costs: usageForm.other_costs !== '' ? Number(usageForm.other_costs) : null,
        notes: usageForm.notes.trim() || null,
      }
      const created = await api.createMachineUsage(payload, fazendaId)
      setUsage(prev => [created, ...prev])
      setShowUsageForm(false)
    } catch (e) { setError(e.message) }
    finally { setSavingUsage(false) }
  }

  async function handleDeleteUsage(record) {
    try {
      await api.deleteMachineUsage(record.id, fazendaId)
      setUsage(prev => prev.filter(x => x.id !== record.id))
      setDeletingUsageId(null)
    } catch (e) { setError(e.message); setDeletingUsageId(null) }
  }

  // ── Maintenance handlers ──────────────────────────────────────────────────
  function openNewMaint() {
    setMaintForm({ machine_id: '', type: MAINTENANCE_TYPES[0], date: new Date().toISOString().split('T')[0], hourmeter: '', cost: '', notes: '' })
    setShowMaintForm(true)
  }

  async function handleSaveMaint() {
    if (!maintForm.machine_id || !maintForm.type || !maintForm.date) return
    setSavingMaint(true)
    try {
      const payload = {
        machine_id: Number(maintForm.machine_id),
        type: maintForm.type,
        date: maintForm.date,
        hourmeter: maintForm.hourmeter !== '' ? Number(maintForm.hourmeter) : null,
        cost: maintForm.cost !== '' ? Number(maintForm.cost) : null,
        notes: maintForm.notes.trim() || null,
      }
      const created = await api.createMaintenance(payload, fazendaId)
      setMaintenance(prev => [created, ...prev])
      setShowMaintForm(false)
    } catch (e) { setError(e.message) }
    finally { setSavingMaint(false) }
  }

  async function handleDeleteMaint(record) {
    try {
      await api.deleteMaintenance(record.id, fazendaId)
      setMaintenance(prev => prev.filter(x => x.id !== record.id))
      setDeletingMaintId(null)
    } catch (e) { setError(e.message); setDeletingMaintId(null) }
  }

  // ── Derived ───────────────────────────────────────────────────────────────
  const tractors = machines.filter(m => m.type === 'trator')
  const implements_ = machines.filter(m => m.type === 'implemento')
  const totalHours = usage.reduce((s, r) => s + (r.hours || 0), 0)
  const totalFuel = usage.reduce((s, r) => s + (r.fuel_liters || 0), 0)
  const totalCosts = usage.reduce((s, r) => s + (r.other_costs || 0), 0)
  const actLabel = (key) => activities.find(a => a.key === key)?.label || key
  const areaName = (id) => areas.find(a => a.id === id)?.name || `Área ${id}`

  // ── No fazenda guard ──────────────────────────────────────────────────────
  if (!fazendaId) {
    return (
      <div style={pageWrap}>
        <div style={header}>
          <Link to="/" style={{ color: '#fff', textDecoration: 'none', fontWeight: 800, fontSize: 18 }}>🌱</Link>
          <span style={{ fontWeight: 800, fontSize: 16 }}>Mecanização</span>
        </div>
        <div style={{ ...content, textAlign: 'center', paddingTop: 80, color: '#6b7280' }}>
          Selecione uma fazenda no Dashboard para usar o módulo de mecanização.
        </div>
      </div>
    )
  }

  return (
    <div style={pageWrap}>
      {/* Header */}
      <div style={header}>
        <Link to="/" style={{ color: '#fff', textDecoration: 'none', fontWeight: 800, fontSize: 18 }}>🌱</Link>
        <span style={{ fontWeight: 800, fontSize: 16 }}>Mecanização</span>
        <span style={{ flex: 1 }} />
        {currentFazenda?.name && (
          <span style={{ fontSize: 13, color: '#bbf7d0', fontWeight: 600 }}>{currentFazenda.name}</span>
        )}
        <Link to="/" style={navLink}>Dashboard</Link>
        {isAdmin && <Link to="/activities" style={navLink}>Atividades</Link>}
        {isAdmin && <Link to="/users" style={navLink}>Usuários</Link>}
        <button onClick={logout} style={{ background: 'none', border: '1px solid #bbf7d0', color: '#bbf7d0', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 13 }}>
          Sair
        </button>
      </div>

      <div style={content}>
        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
            {error}
            <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontWeight: 700 }}>×</button>
          </div>
        )}

        <div style={tabBar}>
          <TabBtn label="🚜 Máquinas" active={tab === 'maquinas'} onClick={() => setTab('maquinas')} />
          <TabBtn label="⏱ Uso" active={tab === 'uso'} onClick={() => setTab('uso')} />
          <TabBtn label="🔧 Manutenção" active={tab === 'manutencao'} onClick={() => setTab('manutencao')} />
        </div>

        {loading ? (
          <div style={{ color: '#6b7280', padding: 40, textAlign: 'center' }}>Carregando...</div>
        ) : (
          <>
            {/* ── TAB: MÁQUINAS ─────────────────────────────────────────── */}
            {tab === 'maquinas' && (
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#14532d' }}>Máquinas e Implementos</h2>
                  {canManageMachines && <button style={greenBtn} onClick={openNewMachine}>+ Nova Máquina</button>}
                </div>

                {machines.length === 0 ? (
                  <div style={{ color: '#6b7280', textAlign: 'center', padding: 40 }}>
                    Nenhuma máquina cadastrada ainda.{isAdmin && ' Clique em "+ Nova Máquina" para começar.'}
                  </div>
                ) : (
                  <>
                    {tractors.length > 0 && (
                      <div style={{ marginBottom: 28 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Tratores</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                          {tractors.map(m => <MachineCard key={m.id} machine={m} canManage={canManageMachines} onEdit={openEditMachine} onToggle={handleToggleMachine} onDelete={setDeletingMachineId} />)}
                        </div>
                      </div>
                    )}
                    {implements_.length > 0 && (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Implementos</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                          {implements_.map(m => <MachineCard key={m.id} machine={m} canManage={canManageMachines} onEdit={openEditMachine} onToggle={handleToggleMachine} onDelete={setDeletingMachineId} />)}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── TAB: USO ──────────────────────────────────────────────── */}
            {tab === 'uso' && (
              <div>
                {/* Filters */}
                <div style={{ ...card, marginBottom: 16 }}>
                  <div style={{ ...row, marginBottom: 0 }}>
                    <div style={formGroup('180px')}>
                      <label style={labelStyle}>Máquina</label>
                      <select style={selectStyle} value={filters.machine_id} onChange={e => setFilters(f => ({ ...f, machine_id: e.target.value }))}>
                        <option value="">Todas</option>
                        {machines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </div>
                    <div style={formGroup('180px')}>
                      <label style={labelStyle}>Área</label>
                      <select style={selectStyle} value={filters.area_id} onChange={e => setFilters(f => ({ ...f, area_id: e.target.value }))}>
                        <option value="">Todas</option>
                        {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                    <div style={formGroup('160px')}>
                      <label style={labelStyle}>Manejo</label>
                      <select style={selectStyle} value={filters.activity_key} onChange={e => setFilters(f => ({ ...f, activity_key: e.target.value }))}>
                        <option value="">Todos</option>
                        {activities.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
                      </select>
                    </div>
                    <div style={formGroup('130px')}>
                      <label style={labelStyle}>De</label>
                      <input type="date" style={inputStyle} value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
                    </div>
                    <div style={formGroup('130px')}>
                      <label style={labelStyle}>Até</label>
                      <input type="date" style={inputStyle} value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, paddingBottom: 2 }}>
                      <button style={greenBtn} onClick={fetchUsage}>Filtrar</button>
                      <button style={grayBtn} onClick={() => { setFilters({ machine_id: '', area_id: '', activity_key: '', date_from: '', date_to: '' }); setTimeout(fetchUsage, 0) }}>Limpar</button>
                    </div>
                  </div>
                </div>

                <div style={card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#14532d' }}>Registros de Uso</h2>
                    <button style={greenBtn} onClick={openNewUsage}>+ Registrar Uso</button>
                  </div>

                  {usage.length === 0 ? (
                    <div style={{ color: '#6b7280', textAlign: 'center', padding: 40 }}>Nenhum registro encontrado.</div>
                  ) : (
                    <>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: '#f0fdf4' }}>
                              {['Data', 'Trator', 'Implemento', 'Área', 'Manejo', 'Horas', 'Diesel (L)', 'Outros Custos', 'Obs', ''].map((h, i) => (
                                <th key={i} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap', borderBottom: '2px solid #d1fae5' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {usage.map(r => (
                              <tr key={r.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                                <td style={{ padding: '8px 10px' }}>{r.machine?.name || '—'}</td>
                                <td style={{ padding: '8px 10px', color: '#6b7280' }}>{r.implement?.name || '—'}</td>
                                <td style={{ padding: '8px 10px' }}>{areaName(r.area_id)}</td>
                                <td style={{ padding: '8px 10px' }}>{actLabel(r.activity_key)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{fmtNum(r.hours)}h</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmtNum(r.fuel_liters)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.other_costs != null ? fmtCurrency(r.other_costs) : '—'}</td>
                                <td style={{ padding: '8px 10px', color: '#6b7280', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.notes || '—'}</td>
                                <td style={{ padding: '8px 10px' }}>
                                  {isAdmin && (
                                    deletingUsageId === r.id ? (
                                      <span style={{ display: 'flex', gap: 4 }}>
                                        <button style={redBtn} onClick={() => handleDeleteUsage(r)}>Confirmar</button>
                                        <button style={smallGrayBtn} onClick={() => setDeletingUsageId(null)}>Cancelar</button>
                                      </span>
                                    ) : (
                                      <button style={smallGrayBtn} onClick={() => setDeletingUsageId(r.id)}>Excluir</button>
                                    )
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {/* Totals */}
                      <div style={{ display: 'flex', gap: 24, marginTop: 16, paddingTop: 16, borderTop: '2px solid #d1fae5', flexWrap: 'wrap' }}>
                        <TotalBadge label="Total de horas" value={`${fmtNum(totalHours)}h`} />
                        {totalFuel > 0 && <TotalBadge label="Diesel estimado" value={`${fmtNum(totalFuel)} L`} />}
                        {totalCosts > 0 && <TotalBadge label="Outros custos" value={fmtCurrency(totalCosts)} />}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ── TAB: MANUTENÇÃO ───────────────────────────────────────── */}
            {tab === 'manutencao' && (
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#14532d' }}>Manutenções</h2>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                      <select style={{ ...selectStyle, width: 'auto' }} value={maintFilter.machine_id} onChange={e => setMaintFilter({ machine_id: e.target.value })}>
                        <option value="">Todas as máquinas</option>
                        {machines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </div>
                    <button style={grayBtn} onClick={fetchMaintenance}>Filtrar</button>
                    <button style={greenBtn} onClick={openNewMaint}>+ Registrar</button>
                  </div>
                </div>

                {maintenance.length === 0 ? (
                  <div style={{ color: '#6b7280', textAlign: 'center', padding: 40 }}>Nenhuma manutenção registrada.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f0fdf4' }}>
                          {['Data', 'Máquina', 'Tipo', 'Horímetro', 'Custo', 'Obs', 'Por', ''].map((h, i) => (
                            <th key={i} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap', borderBottom: '2px solid #d1fae5' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {maintenance.map(r => (
                          <tr key={r.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 600 }}>{r.machine?.name || '—'}</td>
                            <td style={{ padding: '8px 10px' }}>{r.type}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.hourmeter != null ? `${fmtNum(r.hourmeter, 0)}h` : '—'}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.cost != null ? fmtCurrency(r.cost) : '—'}</td>
                            <td style={{ padding: '8px 10px', color: '#6b7280', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.notes || '—'}</td>
                            <td style={{ padding: '8px 10px', color: '#6b7280', fontSize: 12 }}>{r.recorded_by || '—'}</td>
                            <td style={{ padding: '8px 10px' }}>
                              {isAdmin && (
                                deletingMaintId === r.id ? (
                                  <span style={{ display: 'flex', gap: 4 }}>
                                    <button style={redBtn} onClick={() => handleDeleteMaint(r)}>Confirmar</button>
                                    <button style={smallGrayBtn} onClick={() => setDeletingMaintId(null)}>Cancelar</button>
                                  </span>
                                ) : (
                                  <button style={smallGrayBtn} onClick={() => setDeletingMaintId(r.id)}>Excluir</button>
                                )
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── MODAL: Máquina ────────────────────────────────────────────────────── */}
      {showMachineForm && (
        <Modal title={editingMachine ? 'Editar Máquina' : 'Nova Máquina'} onClose={() => setShowMachineForm(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={labelStyle}>Nome *</label>
              <input style={inputStyle} value={machineForm.name} onChange={e => setMachineForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: John Deere 6M" />
            </div>
            <div>
              <label style={labelStyle}>Tipo *</label>
              <select style={selectStyle} value={machineForm.type} onChange={e => setMachineForm(f => ({ ...f, type: e.target.value }))}>
                <option value="trator">Trator</option>
                <option value="implemento">Implemento</option>
              </select>
            </div>
            <div style={row}>
              <div style={formGroup()}>
                <label style={labelStyle}>CV / Potência</label>
                <input type="number" style={inputStyle} value={machineForm.power_cv} onChange={e => setMachineForm(f => ({ ...f, power_cv: e.target.value }))} placeholder="Ex: 125" />
              </div>
              {machineForm.type === 'trator' && (
                <div style={formGroup()}>
                  <label style={labelStyle}>Consumo (L/h)</label>
                  <input type="number" step="0.1" style={inputStyle} value={machineForm.fuel_consumption_per_hour} onChange={e => setMachineForm(f => ({ ...f, fuel_consumption_per_hour: e.target.value }))} placeholder="Ex: 12.5" />
                </div>
              )}
            </div>
            <div>
              <label style={labelStyle}>Descrição</label>
              <input style={inputStyle} value={machineForm.description} onChange={e => setMachineForm(f => ({ ...f, description: e.target.value }))} placeholder="Observações sobre a máquina" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
              <button style={grayBtn} onClick={() => setShowMachineForm(false)}>Cancelar</button>
              <button style={greenBtn} onClick={handleSaveMachine} disabled={savingMachine}>
                {savingMachine ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── MODAL: Confirmar exclusão de máquina ──────────────────────────────── */}
      {deletingMachineId && (
        <Modal title="Confirmar exclusão" onClose={() => setDeletingMachineId(null)}>
          <p style={{ color: '#374151', marginTop: 0 }}>Tem certeza que deseja excluir esta máquina? Todos os registros de uso e manutenção também serão excluídos.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button style={grayBtn} onClick={() => setDeletingMachineId(null)}>Cancelar</button>
            <button style={redBtn} onClick={() => handleDeleteMachine(machines.find(m => m.id === deletingMachineId))}>Excluir</button>
          </div>
        </Modal>
      )}

      {/* ── MODAL: Registrar Uso ──────────────────────────────────────────────── */}
      {showUsageForm && (
        <Modal title="Registrar Uso de Máquina" onClose={() => setShowUsageForm(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={row}>
              <div style={formGroup()}>
                <label style={labelStyle}>Trator *</label>
                <select style={selectStyle} value={usageForm.machine_id} onChange={e => handleUsageMachineChange(e.target.value)}>
                  <option value="">Selecione</option>
                  {tractors.filter(m => m.active).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div style={formGroup()}>
                <label style={labelStyle}>Implemento</label>
                <select style={selectStyle} value={usageForm.implement_id} onChange={e => setUsageForm(f => ({ ...f, implement_id: e.target.value }))}>
                  <option value="">Nenhum</option>
                  {implements_.filter(m => m.active).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            </div>
            <div style={row}>
              <div style={formGroup()}>
                <label style={labelStyle}>Área *</label>
                <select style={selectStyle} value={usageForm.area_id} onChange={e => setUsageForm(f => ({ ...f, area_id: e.target.value }))}>
                  <option value="">Selecione</option>
                  {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div style={formGroup()}>
                <label style={labelStyle}>Manejo/Operação *</label>
                <select style={selectStyle} value={usageForm.activity_key} onChange={e => setUsageForm(f => ({ ...f, activity_key: e.target.value }))}>
                  <option value="">Selecione</option>
                  {activities.filter(a => a.enabled).map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
                </select>
              </div>
            </div>
            <div style={row}>
              <div style={formGroup()}>
                <label style={labelStyle}>Data *</label>
                <input type="date" style={inputStyle} value={usageForm.date} onChange={e => setUsageForm(f => ({ ...f, date: e.target.value }))} />
              </div>
              <div style={formGroup()}>
                <label style={labelStyle}>Horas</label>
                <input type="number" step="0.5" style={inputStyle} value={usageForm.hours} onChange={e => handleUsageHoursChange(e.target.value)} placeholder="Ex: 4" />
              </div>
            </div>
            <div style={row}>
              <div style={formGroup()}>
                <label style={labelStyle}>
                  Diesel (L)
                  {machines.find(m => m.id === Number(usageForm.machine_id))?.fuel_consumption_per_hour && (
                    <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>
                      (auto: {machines.find(m => m.id === Number(usageForm.machine_id)).fuel_consumption_per_hour} L/h)
                    </span>
                  )}
                </label>
                <input type="number" step="0.1" style={inputStyle} value={usageForm.fuel_liters} onChange={e => setUsageForm(f => ({ ...f, fuel_liters: e.target.value }))} placeholder="Ex: 50" />
              </div>
              <div style={formGroup()}>
                <label style={labelStyle}>Outros Custos (R$)</label>
                <input type="number" step="0.01" style={inputStyle} value={usageForm.other_costs} onChange={e => setUsageForm(f => ({ ...f, other_costs: e.target.value }))} placeholder="Ex: 350.00" />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Observações</label>
              <input style={inputStyle} value={usageForm.notes} onChange={e => setUsageForm(f => ({ ...f, notes: e.target.value }))} placeholder="Anotações opcionais" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
              <button style={grayBtn} onClick={() => setShowUsageForm(false)}>Cancelar</button>
              <button style={greenBtn} onClick={handleSaveUsage} disabled={savingUsage}>
                {savingUsage ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── MODAL: Registrar Manutenção ───────────────────────────────────────── */}
      {showMaintForm && (
        <Modal title="Registrar Manutenção" onClose={() => setShowMaintForm(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={labelStyle}>Máquina *</label>
              <select style={selectStyle} value={maintForm.machine_id} onChange={e => setMaintForm(f => ({ ...f, machine_id: e.target.value }))}>
                <option value="">Selecione</option>
                {machines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div style={row}>
              <div style={formGroup()}>
                <label style={labelStyle}>Tipo *</label>
                <select style={selectStyle} value={maintForm.type} onChange={e => setMaintForm(f => ({ ...f, type: e.target.value }))}>
                  {MAINTENANCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={formGroup()}>
                <label style={labelStyle}>Data *</label>
                <input type="date" style={inputStyle} value={maintForm.date} onChange={e => setMaintForm(f => ({ ...f, date: e.target.value }))} />
              </div>
            </div>
            <div style={row}>
              <div style={formGroup()}>
                <label style={labelStyle}>Horímetro (h)</label>
                <input type="number" step="1" style={inputStyle} value={maintForm.hourmeter} onChange={e => setMaintForm(f => ({ ...f, hourmeter: e.target.value }))} placeholder="Ex: 2450" />
              </div>
              <div style={formGroup()}>
                <label style={labelStyle}>Custo (R$)</label>
                <input type="number" step="0.01" style={inputStyle} value={maintForm.cost} onChange={e => setMaintForm(f => ({ ...f, cost: e.target.value }))} placeholder="Ex: 250.00" />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Observações</label>
              <input style={inputStyle} value={maintForm.notes} onChange={e => setMaintForm(f => ({ ...f, notes: e.target.value }))} placeholder="Detalhes da manutenção" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
              <button style={grayBtn} onClick={() => setShowMaintForm(false)}>Cancelar</button>
              <button style={greenBtn} onClick={handleSaveMaint} disabled={savingMaint}>
                {savingMaint ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MachineCard({ machine, canManage, onEdit, onToggle, onDelete }) {
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: machine.active ? '#fff' : '#f9fafb', opacity: machine.active ? 1 : 0.65 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#14532d', marginBottom: 4 }}>{machine.name}</div>
          <MachineBadge type={machine.type} />
        </div>
        {!machine.active && <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>INATIVO</span>}
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 12 }}>
        {machine.power_cv && <span>⚡ {machine.power_cv} CV</span>}
        {machine.fuel_consumption_per_hour && <span>⛽ {machine.fuel_consumption_per_hour} L/h</span>}
        {machine.description && <span>📝 {machine.description}</span>}
      </div>
      {canManage && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={smallGrayBtn} onClick={() => onEdit(machine)}>Editar</button>
          <button style={{ ...smallGrayBtn, color: machine.active ? '#dc2626' : '#16a34a' }} onClick={() => onToggle(machine)}>
            {machine.active ? 'Inativar' : 'Ativar'}
          </button>
          <button style={{ ...smallGrayBtn, background: '#fee2e2', color: '#dc2626' }} onClick={() => onDelete(machine.id)}>Excluir</button>
        </div>
      )}
    </div>
  )
}

function TotalBadge({ label, value }) {
  return (
    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#14532d' }}>{value}</div>
    </div>
  )
}
